import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  PlusCircle,
  Filter as FilterIcon,
  Plane,
  Building2,
  ShieldAlert,
  Upload,
  Loader2,
  FileDown,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import DynamicFormModal from '../../components/DynamicFormModal';
import FilterDrawer from '../../components/FilterDrawer';
import AppliedFilters from '../../components/AppliedFilters';
import ExportExcelButton from '../../components/ExportExcelButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../components/ui/toaster.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import {
  fetchAirports,
  createAirport,
  updateAirport,
  deleteAirport,
  toggleAirportStatus,
  importAirports,
  exportAirports,
} from './Services/airportsService.js';
import { airportFormFields, columnsExcel, CONTINENTES } from './utils/airportsColumns.jsx';

const ITEMS_PER_PAGE = 10;
const SORTABLE_COLUMNS = new Set([
  'nombre', 'iata', 'icao', 'ciudad', 'pais', 'continente', 'estado', 'updatedAt',
]);
const SORT_DIRS = new Set([1, -1, 'asc', 'desc']);

// Tamaño de cada chunk al importar. Mantenemos los requests < ~2MB
// para que el back no se cuelgue y el front muestre progreso.
const IMPORT_BATCH_SIZE = 200;

/**
 * Genera y descarga una plantilla Excel para la importación masiva
 * de aeropuertos. Incluye los headers esperados y tres filas de
 * ejemplo que muestran los formatos válidos (con/sin ICAO,
 * Inactivo explícito).
 */
async function downloadAirportTemplate() {
  const XLSX = await import('xlsx');
  const headers = ['Nombre', 'IATA', 'ICAO', 'Ciudad', 'País', 'Continente', 'Estado'];
  const sampleRows = [
    {
      Nombre: 'John F Kennedy International',
      IATA: 'JFK',
      ICAO: 'KJFK',
      Ciudad: 'New York',
      País: 'Estados Unidos',
      Continente: 'América del Norte',
      Estado: 'Activo',
    },
    {
      Nombre: 'Heathrow',
      IATA: 'LHR',
      ICAO: 'EGLL',
      Ciudad: 'London',
      País: 'Reino Unido',
      Continente: 'Europa',
      Estado: 'Activo',
    },
    {
      // Ejemplo SIN ICAO (opcional) y en estado Inactivo.
      Nombre: 'Aeropuerto de prueba',
      IATA: 'ZZZ',
      ICAO: '',
      Ciudad: 'Ciudad Demo',
      País: 'Argentina',
      Continente: 'América del Sur',
      Estado: 'Inactivo',
    },
  ];
  const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
  // Ancho de columnas sugerido
  ws['!cols'] = headers.map((h) => ({ wch: Math.max(20, h.length + 4) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Aeropuertos');
  XLSX.writeFile(wb, 'plantilla_aeropuertos.xlsx');
}

const formatDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

export default function AirportsPage() {
  const toast = useToast();
  const { tenant, isPlatformMode } = useAuth();
  const fileInputRef = useRef(null);

  // --- Estado principal ---
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ processed: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ iata: 1 });

  // --- Modales / drawers ---
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // --- Datos: cargar aeropuertos ---
  const fetchAndSetAirports = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await fetchAirports({
        limit: itemsPerPage,
        offset: currentPage,
        query: filters,
        sort,
      });
      setDisplayedData(r?.data || []);
      setTotalItems(r?.totalRecords || 0);
    } catch (e) {
      toast.error('Error al cargar aeropuertos', e?.message || 'Intenta nuevamente');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, filters, sort, toast]);

  useEffect(() => {
    fetchAndSetAirports();
  }, [fetchAndSetAirports]);

  // --- Definición de filtros (Drawer) ---
  const filterFields = useMemo(
    () => [
      {
        id: 'estado',
        label: 'Estado',
        type: 'select',
        options: [
          { value: 'Activo', label: 'Activo' },
          { value: 'Inactivo', label: 'Inactivo' },
        ],
      },
      { id: 'pais', label: 'País', type: 'text' },
      { id: 'ciudad', label: 'Ciudad', type: 'text' },
      {
        id: 'continente',
        label: 'Continente',
        type: 'select',
        options: [
          { value: '', label: '— Todos —' },
          ...CONTINENTES.map((c) => ({ value: c, label: c })),
        ],
      },
      { id: 'nombre', label: 'Nombre contiene', type: 'text' },
      { id: 'iata', label: 'IATA contiene', type: 'text' },
      { id: 'icao', label: 'ICAO contiene', type: 'text' },
    ],
    [],
  );

  // --- Columnas de la DataTable ---
  const dataColumns = useMemo(
    () => [
      { id: 'nombre', label: 'Nombre', sortable: true, width: '22%' },
      { id: 'ciudad', label: 'Ciudad', sortable: true, width: '13%' },
      { id: 'pais', label: 'País', sortable: true, width: '13%' },
      {
        id: 'iata',
        label: 'IATA',
        sortable: true,
        width: '80px',
        render: (row) => (
          <code className="px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-mono font-semibold">
            {row.iata || '—'}
          </code>
        ),
      },
      {
        id: 'icao',
        label: 'ICAO',
        sortable: true,
        width: '80px',
        render: (row) => (
          <code className="px-2 py-0.5 rounded bg-muted text-foreground text-xs font-mono">
            {row.icao || '—'}
          </code>
        ),
      },
      {
        id: 'continente',
        label: 'Continente',
        sortable: true,
        render: (row) =>
          row.continente || <span className="text-muted-foreground text-xs">—</span>,
      },
      {
        id: 'estado',
        label: 'Estado',
        sortable: true,
        width: '110px',
        type: 'status',
      },
      {
        id: 'updatedAt',
        label: 'Actualizado',
        sortable: true,
        width: '150px',
        render: (row) => (
          <span className="text-xs text-muted-foreground" title={row.updatedAt || ''}>
            {formatDateTime(row.updatedAt)}
          </span>
        ),
      },
    ],
    [],
  );

  const tableColumns = useMemo(
    () => [
      ...dataColumns,
      {
        id: 'actions',
        key: 'actions',
        label: 'Acciones',
        actions: [
          {
            key: 'edit',
            icon: 'Edit',
            tooltip: 'Editar aeropuerto',
            className: 'hover:bg-foreground hover:text-background',
          },
          {
            key: 'toggle',
            icon: 'Power',
            tooltip: (row) => (row.estado === 'Activo' ? 'Inactivar' : 'Activar'),
            className: 'hover:bg-amber-500 hover:text-white',
          },
          {
            key: 'delete',
            icon: 'Trash',
            tooltip: 'Eliminar aeropuerto',
            className: 'hover:bg-destructive hover:text-destructive-foreground',
          },
        ],
      },
    ],
    [dataColumns],
  );

  // --- Manejo de sort por click en header ---
  const handleSort = useCallback((columnId) => {
    if (!SORTABLE_COLUMNS.has(columnId)) return;
    setSort((prev) => {
      const current = prev[columnId];
      if (current === 1 || current === 'asc') return { [columnId]: -1 };
      if (current === -1 || current === 'desc') return { iata: 1 };
      return { [columnId]: 1 };
    });
    setCurrentPage(1);
  }, []);

  // --- Handlers ---
  const handlePageChange = (page) => setCurrentPage(page);
  const handleLimitChange = (n) => {
    setItemsPerPage(n);
    setCurrentPage(1);
  };

  const openCreate = () => {
    setCurrentItem(null);
    setIsModalOpen(true);
  };
  const openEdit = (row) => {
    setCurrentItem(row);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSave = async (formData) => {
    try {
      const payload = {
        ...formData,
        iata: (formData.iata || '').toUpperCase(),
        icao: formData.icao ? formData.icao.toUpperCase() : undefined,
      };
      const r = currentItem
        ? await updateAirport(currentItem._id, payload)
        : await createAirport(payload);
      if (r?.success) {
        toast.success(currentItem ? 'Aeropuerto actualizado' : 'Aeropuerto creado');
        closeModal();
        fetchAndSetAirports();
      } else {
        toast.error('No se pudo guardar', r?.message);
      }
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    }
  };

  const handleDeleteConfirmation = useCallback((row) => {
    setItemToDelete(row);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      const r = await deleteAirport(itemToDelete._id);
      if (r?.success) {
        toast.success('Aeropuerto eliminado', `${itemToDelete.iata} · ${itemToDelete.nombre}`);
        if (displayedData.length === 1 && currentPage > 1) {
          setCurrentPage((p) => p - 1);
        } else {
          fetchAndSetAirports();
        }
      } else {
        toast.error('No se pudo eliminar', r?.message);
      }
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleToggleStatus = useCallback(
    async (row) => {
      const r = await toggleAirportStatus(row._id);
      if (r?.success) {
        const isActive = r.data?.estado === 'Activo';
        toast.success(
          isActive ? 'Aeropuerto activado' : 'Aeropuerto inactivado',
          `${row.iata} · ${row.nombre}`,
        );
        fetchAndSetAirports();
      } else {
        toast.error('No se pudo cambiar el estado', r?.message);
      }
    },
    [fetchAndSetAirports, toast],
  );

  const handleTableAction = useCallback(
    (actionType, row) => {
      if (actionType === 'edit') return openEdit(row);
      if (actionType === 'delete') return handleDeleteConfirmation(row);
      if (actionType === 'toggle') return handleToggleStatus(row);
    },
    [handleDeleteConfirmation, handleToggleStatus],
  );

  // --- Importar Excel ---
  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIsImporting(true);
    setImportProgress({ processed: 0, total: 0 });
    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const firstSheet = wb.SheetNames[0];
      const sheet = wb.Sheets[firstSheet];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (rows.length === 0) {
        toast.error('El archivo no contiene filas');
        return;
      }

      // Segmentamos en chunks de IMPORT_BATCH_SIZE para no saturar
      // al back con un solo body enorme y mostrar progreso al usuario.
      const total = rows.length;
      setImportProgress({ processed: 0, total });
      let totalCreated = 0;
      let totalFailed = 0;
      const allErrors = [];

      for (let i = 0; i < rows.length; i += IMPORT_BATCH_SIZE) {
        const batch = rows.slice(i, i + IMPORT_BATCH_SIZE);
        const batchNumber = Math.floor(i / IMPORT_BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(rows.length / IMPORT_BATCH_SIZE);
        try {
          const r = await importAirports(batch);
          if (r?.success) {
            const data = r.data || {};
            totalCreated += data.created || 0;
            totalFailed += data.failed || 0;
            if (Array.isArray(data.errors) && data.errors.length > 0) {
              // Re-mapeamos los índices: el backend los devuelve
              // dentro del batch, los nuestros son absolutos.
              const offset = i;
              allErrors.push(
                ...data.errors.map((e) => ({ ...e, index: (e.index ?? 0) + offset })),
              );
            }
          } else {
            totalFailed += batch.length;
            allErrors.push({
              index: i,
              row: { batch: batchNumber },
              reason: r?.message || 'Error desconocido',
            });
          }
        } catch (batchErr) {
          totalFailed += batch.length;
          allErrors.push({
            index: i,
            row: { batch: batchNumber },
            reason: batchErr?.message || 'Error de red',
          });
        }
        setImportProgress({ processed: Math.min(i + batch.length, total), total });
      }

      toast.success(
        `Importación completada: ${totalCreated} creados, ${totalFailed} con error`,
        `${total} filas en ${Math.ceil(total / IMPORT_BATCH_SIZE)} lote(s)`,
      );
      if (allErrors.length > 0) {
        // Muestra los primeros 5 errores en consola.
        // eslint-disable-next-line no-console
        console.warn(`[airports.import] ${allErrors.length} error(es):`, allErrors.slice(0, 10));
      }
      fetchAndSetAirports();
    } catch (err) {
      toast.error('Error al leer el archivo', err?.message);
    } finally {
      setIsImporting(false);
      setImportProgress({ processed: 0, total: 0 });
    }
  };

  // --- Filtros ---
  const handleApplyFilters = (applied) => {
    setFilters(applied);
    setCurrentPage(1);
  };
  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };
  const handleClearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <Plane className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-2xl">Parametrización de Aeropuertos</CardTitle>
                {tenant ? (
                  <Badge variant="secondary" className="flex items-center gap-1 font-normal">
                    <Building2 className="h-3 w-3" />
                    {tenant.nombre || tenant.slug}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 font-normal text-amber-600 border-amber-300"
                  >
                    <ShieldAlert className="h-3 w-3" />
                    Modo plataforma
                  </Badge>
                )}
              </div>
              <CardDescription>
                {tenant
                  ? `Catálogo de aeropuertos del operador ${tenant.nombre || tenant.slug}.`
                  : 'Catálogo global de aeropuertos (modo plataforma).'}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <ExportExcelButton
              service={exportAirports}
              columns={columnsExcel}
              fileName={`aeropuertos_${new Date().toISOString().slice(0, 10)}`}
              filters={filters}
            />
            <Button
              variant="outline"
              onClick={downloadAirportTemplate}
              title="Descargar plantilla con los headers esperados"
              className="border-slate-500 text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-900/30"
            >
              <FileDown className="mr-2 h-4 w-4" />
              Plantilla
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelected}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={handleImportClick}
              disabled={isImporting}
              className="border-blue-600 text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
            >
              {isImporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              {isImporting
                ? importProgress.total > 0
                  ? `Importando ${importProgress.processed}/${importProgress.total}…`
                  : 'Importando…'
                : 'Importar Excel'}
            </Button>
            <Button variant="outline" onClick={() => setIsDrawerOpen(true)}>
              <FilterIcon className="mr-2 h-4 w-4" /> Filtros
            </Button>
            <Button onClick={openCreate}>
              <PlusCircle className="mr-2 h-4 w-4" /> Crear Aeropuerto
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <AppliedFilters
            filters={filters}
            fields={filterFields}
            onRemoveFilter={handleRemoveFilter}
            onClear={handleClearFilters}
          />

          <DataTable
            columns={tableColumns}
            data={displayedData}
            isLoading={isLoading}
            onAction={handleTableAction}
            sort={sort}
            onSort={handleSort}
            emptyMessage="Sin aeropuertos"
            emptyHint="No hay aeropuertos que coincidan con los filtros aplicados. Crea uno nuevo o ajusta los filtros."
          />

          {!isLoading && totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </CardContent>
      </Card>

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={filterFields}
        initialFilters={filters}
        onApply={handleApplyFilters}
      />

      <DynamicFormModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSave}
        fields={airportFormFields}
        item={currentItem}
        title={currentItem ? 'Editar Aeropuerto' : 'Crear Aeropuerto'}
        maxWidth="max-w-2xl"
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar aeropuerto?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete && (
                <>
                  Se eliminará <strong>{itemToDelete.nombre}</strong> (
                  <code className="text-xs px-1 py-0.5 rounded bg-muted">
                    {itemToDelete.iata}
                  </code>
                  ). Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
